import { useCallback, useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { environment } from '~app/environment';

import type { SetupLoanFormValues } from '~features/setup-loan';
import { SetupLoan } from '~features/setup-loan/ui/setup-loan';

import { analytics } from '~entities/amplitude-analytics';
import { userPhoneAtom } from '~entities/auth';
import type { SearchOfferResponseSuccess } from '~entities/offer';
import {
  offerKeys,
  useMutateAsyncSearchOfferByPhone,
  useSearchOfferByPhone,
} from '~entities/offer';
import { type OrderItem } from '~entities/order';
import type { Order } from '~entities/order';
import { OrderItemUI } from '~entities/order/ui/order-item/order-item';
import { calculateGracePeriod } from '~entities/product';
import { productKeys, useProduct } from '~entities/product/api';

import { nonNullableValue } from '~shared/types/non-nullable-value';
import { HeaderWithLogo } from '~shared/ui/header-with-logo';
import { Island } from '~shared/ui/island';
import { Screen } from '~shared/ui/screen';
import { ScreenTitle } from '~shared/ui/screen-title';

import type { LoanCalculatorFormValues } from '.';
import { Checkbox } from '@breeze/rhf-adapters';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import { Offer } from '../offer';

export type FormValues = {
  items: OrderItem[];
  skipValidation?: boolean;
  offerId?: string;
  productId?: string;
  gracePeriodInMonths?: number;
} & SetupLoanFormValues;

type Props = {
  order: Order;
  initialValue?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
  applicationId?: string;
};

export const LoanCalculator: React.FC<Props> = ({
  order,
  initialValue,
  onSubmit,
  applicationId,
}) => {
  const canSkipServerValidation = environment.CAN_SKIP_VALIDATION;

  useEffect(() => {
    analytics.track.calculatorOpened(order.successUrl);
  }, [order.successUrl]);

  const methods = useForm<LoanCalculatorFormValues>({
    mode: 'all',
    defaultValues: {
      ...initialValue,
      items: order.orderItems,
      skipValidation: false,
    },
  });
  const userPhone = useAtomValue(userPhoneAtom);

  const queryClient = useQueryClient();
  const [offerPhone, setOfferPhone] = useState<string | null>(
    initialValue?.offerId ? userPhone : null
  );
  const { mutateAsync: mutateAsyncOfferByPhone } =
    useMutateAsyncSearchOfferByPhone(offerPhone);
  const { data: offer, isLoading: isOfferLoading } = useSearchOfferByPhone(
    { phone: offerPhone },
    { enabled: !!offerPhone }
  );
  const hasOfferByPhone = offer && offer.status !== 'NOT_FOUND';
  const { data: offerProduct, isLoading: isProductLoading } = useProduct(
    {
      productId: (offer as SearchOfferResponseSuccess)?.posProductId,
    },
    { disable: !hasOfferByPhone }
  );

  const resetOfferDetails = useCallback(() => {
    queryClient.removeQueries({
      queryKey: productKeys.byId(nonNullableValue(offerProduct?.productId)),
    });
    queryClient.removeQueries({
      queryKey: offerKeys.offerByPhone(offerPhone),
    });
  }, [offerPhone, offerProduct?.productId, queryClient]);

  useEffect(() => {
    if (offerPhone === null) {
      resetOfferDetails();
    }
  }, [offerPhone, resetOfferDetails]);

  const handleSubmit = async (values: FormValues) => {
    // we need to check if the offer is still active
    // before creating application
    if (
      hasOfferByPhone &&
      !(initialValue?.offerId && initialValue?.offerId === offer?.id)
    ) {
      const checkOfferResult = await mutateAsyncOfferByPhone(
        nonNullableValue(offerPhone)
      ).catch(() => ({ status: 'NOT_FOUND' }));

      if (checkOfferResult.status === 'NOT_FOUND') {
        resetOfferDetails();
        return;
      }
    }
    const offerDetails = hasOfferByPhone
      ? { offerId: offer?.id, productId: offer?.posProductId }
      : { offerId: undefined, productId: undefined };

    const gracePeriodInMonths =
      order.gracePeriodAvailable && values.period
        ? calculateGracePeriod(order.paymentAmount, +values.period)
        : undefined;

    onSubmit({
      ...values,
      ...offerDetails,
      gracePeriodInMonths,
    });
  };

  return (
    <Screen header={<HeaderWithLogo />}>
      <div className="flex flex-col gap-5">
        <ScreenTitle title="Let’s calculate your loan" />
        <Island>
          <div className="flex flex-col gap-5">
            {order.orderItems.map((item) => (
              <OrderItemUI item={item} key={`${item.name}_${item.isUsed}`} />
            ))}
          </div>
        </Island>
        <Island style={{ paddingBottom: 0 }}>
          <Offer
            phone={offerPhone}
            applicationId={applicationId}
            onCancelOffer={() => {
              setOfferPhone(null);
            }}
            isLoading={isProductLoading}
            onPhoneUpdate={setOfferPhone}
            onOfferError={resetOfferDetails}
          />
        </Island>
        <Island>
          <form onSubmit={methods.handleSubmit(handleSubmit)}>
            <FormProvider {...methods}>
              <SetupLoan
                disableSubmit={isOfferLoading}
                totalPrice={order.paymentAmount}
                loanParams={offerProduct ?? order.product}
              />
            </FormProvider>
            {canSkipServerValidation && (
              <div className="mt-4">
                <Controller
                  name="skipValidation"
                  control={methods.control}
                  render={(props) => (
                    <Checkbox {...props}>
                      Move the application to the status APPROVED (dev envs
                      only)
                    </Checkbox>
                  )}
                />
              </div>
            )}
          </form>
        </Island>
      </div>
    </Screen>
  );
};
